import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import ZoomableImage from "./ZoomableImage";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const ImageModal = ({ isOpen, onClose, imageUrl, imageAlt = "Image" }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentImage, setCurrentImage] = useState(0);

    useEffect(() => {
        // Handle keyboard events
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.body.style.overflow = "hidden";
            window.addEventListener("keydown", handleKeyDown);
            setIsVisible(true);
        } else {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
            setTimeout(() => setIsVisible(false), 300);
        }

        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isVisible && !isOpen) return null;

    // Handle clicking the previous image
    const onClickPrev = () => {
        if (currentImage > 0) {
            setCurrentImage(currentImage - 1);
        }
    };

    // Handle clicking the next image
    const onClickNext = () => {
        if (currentImage < imageUrl.length - 1) {
            setCurrentImage(currentImage + 1);
        }
    };

    return (
        <div
            className={`tw-fixed tw-inset-0 tw-z-[999] tw-flex tw-items-center tw-justify-center tw-bg-black/80 tw-backdrop-blur-sm tw-transition-opacity tw-duration-300 ${
                isOpen ? "tw-opacity-100" : "tw-opacity-0"
            }`}
            onClick={onClose}
        >
            <div
                className={`tw-relative tw-h-[90vh] tw-w-[90vw] tw-max-w-7xl tw-transform tw-rounded-lg tw-bg-gray-900 tw-shadow-xl tw-transition-all tw-duration-300 ${
                    isOpen ? "tw-scale-100" : "tw-scale-95"
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="tw-absolute tw-right-2 sm:tw-right-4 tw-top-2 sm:tw-top-4 tw-z-10 tw-rounded-full tw-bg-black/60 tw-p-2 tw-text-white hover:tw-bg-black/80 tw-transition-colors tw-shadow-lg"
                    aria-label="Close"
                >
                    <X size={20} />
                </button>

                <div className="tw-h-full tw-w-full">
                    <ZoomableImage src={imageUrl[currentImage].image_url} alt={imageAlt} />
                </div>

                <div className="tw-absolute tw-top-2 sm:tw-top-4 tw-left-2 sm:tw-left-4 tw-rounded-md tw-bg-black/60 tw-px-3 tw-py-1 tw-text-xs sm:tw-text-sm tw-text-white tw-hidden sm:tw-block">
                    Scroll to zoom in/out
                </div>

                {currentImage != 0 && (
                    <span
                        onClick={onClickPrev}
                        className="tw-rounded-full tw-absolute tw-left-2 sm:tw-left-10 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-black/40 hover:tw-bg-black/60 tw-text-white tw-cursor-pointer tw-transition-all tw-shadow-lg"
                    >
                        <FaChevronLeft className="tw-text-[20px] sm:tw-text-[24px] tw-m-2" />
                    </span>
                )}

                {/* Next Button */}
                {currentImage != imageUrl.length - 1 && (
                    <span
                        onClick={onClickNext}
                        className="tw-rounded-full tw-absolute tw-right-2 sm:tw-right-10 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-black/40 hover:tw-bg-black/60 tw-text-white tw-cursor-pointer tw-transition-all tw-shadow-lg"
                    >
                        <FaChevronRight className="tw-text-[20px] sm:tw-text-[24px] tw-m-2" />
                    </span>
                )}
                {/* Image Counter */}
                <div className="tw-absolute tw-bottom-2 sm:tw-bottom-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-bg-black/60 tw-text-white tw-rounded-lg tw-px-3 tw-py-1 tw-text-xs sm:tw-text-sm tw-font-medium tw-backdrop-blur-sm">
                    {currentImage + 1}/{imageUrl.length}
                </div>
            </div>
        </div>
    );
};

export default ImageModal;
